import Swal from "sweetalert2";
import { api } from "../../../api/Middleware";
import { COLUMNS_URL, CONTACTS_URL } from "../../../api/Urls";

// Buscar contatos com paginação para uma coluna específica
export const fetchContactsWithPagination = async (
  columnId,
  offset = 0,
  limit = 10
) => {
  try {
    const response = await api.get(
      `${CONTACTS_URL}?column=${columnId}&offset=${offset}&limit=${limit}`
    );

    return {
      results: response.data.results || [],
      count: response.data.count || 0,
      has_next: response.data.has_next || false,
      has_previous: response.data.has_previous || false,
      next_offset: response.data.next_offset,
      previous_offset: response.data.previous_offset,
    };
  } catch (err) {
    console.error(`Erro ao buscar contatos da coluna ${columnId}:`, err);
    return {
      results: [],
      count: 0,
      has_next: false,
      has_previous: false,
      next_offset: null,
      previous_offset: null,
    };
  }
};

// Carregar mais contatos para uma coluna (usado no scroll infinito)
export const loadMoreContacts = async (columnId, offset, limit = 10) => {
  try {
    const response = await api.get(
      `${CONTACTS_URL}?column=${columnId}&offset=${offset}&limit=${limit}`
    );

    return {
      results: response.data.results || [],
      count: response.data.count || 0,
      has_next: response.data.has_next || false,
      has_previous: response.data.has_previous || false,
      next_offset: response.data.next_offset,
      previous_offset: response.data.previous_offset,
    };
  } catch (err) {
    console.error(`Erro ao carregar mais contatos da coluna ${columnId}:`, err);
    throw err;
  }
};

// Buscar dados da API com suporte a paginação
export const fetchData = async (
  setColumns,
  setContactsByColumn,
  setLoading,
  setError,
  setOffsetByColumn,
  setHasMoreByColumn,
  setTotalContactsByColumn
) => {
  try {
    setLoading(true);

    // 1. Primeiro buscar todas as colunas
    const response = await api.get(`${COLUMNS_URL}`);
    let columnsData = response.data.results || [];

    // 3. Ordenar colunas com base no campo order
    columnsData.sort((a, b) => a.order - b.order);

    // 4. Armazenar colunas no estado
    setColumns(columnsData);

    // 5. Buscar contatos para cada coluna com paginação (primeira página)
    const contactsMap = {};
    const offsetMap = {};
    const hasMoreMap = {};
    const totalMap = {};

    // Fazer todas as requisições em paralelo para melhorar a performance
    const contactPromises = columnsData.map(async (column) => {
      try {
        // Buscar primeira página de contatos da coluna
        const paginatedResponse = await fetchContactsWithPagination(
          column.id,
          0,
          10
        );

        // Ordenar contatos pela ordem definida no backend
        const contactsData = paginatedResponse.results || [];
        contactsData.sort((a, b) => a.order - b.order);

        // Armazenar no mapa
        contactsMap[column.id] = contactsData;
        offsetMap[column.id] = 10; // Próximo offset será 10
        hasMoreMap[column.id] = paginatedResponse.has_next;
        totalMap[column.id] = paginatedResponse.count;
      } catch (err) {
        console.error(`Erro ao buscar contatos da coluna ${column.id}:`, err);
        contactsMap[column.id] = []; // Inicializar como array vazio em caso de erro
        offsetMap[column.id] = 0;
        hasMoreMap[column.id] = false;
        totalMap[column.id] = 0;
      }
    });

    // Aguardar todas as requisições de contatos terminarem
    await Promise.all(contactPromises);

    // 6. Atualizar os estados com os dados organizados
    setContactsByColumn(contactsMap);
    setOffsetByColumn(offsetMap);
    setHasMoreByColumn(hasMoreMap);
    setTotalContactsByColumn(totalMap);

    setLoading(false);
  } catch (err) {
    console.error("Erro ao buscar dados:", err);
    setError("Não foi possível carregar os dados. Tente novamente mais tarde.");
    setLoading(false);

    Swal.fire({
      title: "Erro!",
      text: "Não foi possível carregar os dados. Tente novamente mais tarde.",
      icon: "error",
      confirmButtonText: "OK",
    });
  }
};

// Função para buscar contatos de uma coluna específica (versão antiga - mantida para compatibilidade)
export const fetchColumnContacts = async (columnId) => {
  try {
    const response = await api.get(`${CONTACTS_URL}?column=${columnId}`);
    return response.data.results || [];
  } catch (err) {
    console.error(`Erro ao buscar contatos da coluna ${columnId}:`, err);
    return [];
  }
};

// Função para buscar todos os contatos de todas as colunas (versão antiga - mantida para compatibilidade)
export const fetchAllContacts = async (columns) => {
  try {
    const newContactsByColumn = {};

    // Cria um array de promessas para buscar os contatos de cada coluna
    const promises = columns.map(async (column) => {
      const contacts = await fetchColumnContacts(column.id);
      newContactsByColumn[column.id] = contacts;
    });

    // Aguarda todas as requisições terminarem
    await Promise.all(promises);

    return newContactsByColumn;
  } catch (err) {
    console.error("Erro ao buscar todos os contatos:", err);
    return {};
  }
};

export const handleBulkDelete = (
  selectedContacts,
  contactsByColumn,
  setContactsByColumn,
  setSelectedContacts
) => {
  if (selectedContacts.length === 0) return;

  Swal.fire({
    title: "Confirmar exclusão",
    text: `Esta ação não poderá ser revertida! Deseja excluir ${selectedContacts.length} contatos selecionados?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sim, excluir",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#dc3545",
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        const response = await api.post(`${CONTACTS_URL}bulk_delete/`, {
          ids: selectedContacts,
        });

        // Verificar se a resposta foi bem-sucedida
        if (response.status !== 200) {
          throw new Error("Falha na exclusão de contatos");
        }

        // Atualizar estado local
        const newContactsByColumn = { ...contactsByColumn };

        Object.keys(newContactsByColumn).forEach((columnId) => {
          newContactsByColumn[columnId] = newContactsByColumn[columnId].filter(
            (contact) => !selectedContacts.includes(contact.id)
          );
        });

        setContactsByColumn(newContactsByColumn);
        setSelectedContacts([]);

        Swal.fire({
          title: "Excluídos!",
          text: "Os contatos foram excluídos com sucesso.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } catch (err) {
        console.error("Erro ao excluir contatos:", err);

        Swal.fire({
          title: "Erro!",
          text: "Não foi possível excluir os contatos selecionados.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  });
};

// Adicionar essas funções ao services.js para uma abordagem mais eficiente

/**
 * Move um contato específico para uma nova coluna e posição
 * @param {number} contactId - ID do contato
 * @param {number} columnId - ID da nova coluna
 * @param {number} order - Nova posição
 */
export const moveContact = async (contactId, columnId, order) => {
  try {
    const response = await api.post(`${CONTACTS_URL}reorder/`, [
      {
        id: contactId,
        columnId: columnId,
        order: order,
      },
    ]);

    return response.data;
  } catch (err) {
    console.error(`Erro ao mover contato ${contactId}:`, err);
    throw err;
  }
};

/**
 * Alternativa usando PATCH para atualizar um contato específico
 * Esta pode ser uma abordagem ainda mais eficiente
 * @param {number} contactId - ID do contato
 * @param {number} columnId - ID da nova coluna
 * @param {number} order - Nova posição
 */
export const updateContactPosition = async (contactId, columnId, order) => {
  try {
    const response = await api.patch(`${CONTACTS_URL}${contactId}/`, {
      column: columnId,
      order: order,
    });

    return response.data;
  } catch (err) {
    console.error(`Erro ao atualizar posição do contato ${contactId}:`, err);
    throw err;
  }
};

/**
 * Função para reordenar apenas os contatos afetados por uma mudança
 * @param {Array} affectedContacts - Array com os contatos que precisam ter a ordem atualizada
 */
export const reorderAffectedContacts = async (affectedContacts) => {
  try {
    if (affectedContacts.length === 0) {
      return { status: "Nenhum contato para reordenar" };
    }

    const response = await api.post(
      `${CONTACTS_URL}reorder/`,
      affectedContacts
    );
    return response.data;
  } catch (err) {
    console.error("Erro ao reordenar contatos afetados:", err);
    throw err;
  }
};

// Versão atualizada da função handleSaveContact para usar a nova abordagem
export const handleSaveContact = async (
  updatedContact,
  contactsByColumn,
  setContactsByColumn,
  setContactModalOpen,
  setCurrentContact
) => {
  try {
    if (!updatedContact || !updatedContact.id) {
      console.error("Dados de contato inválidos:", updatedContact);
      Swal.fire({
        title: "Erro!",
        text: "Dados de contato inválidos. Tente novamente.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    // Usar PATCH em vez de atualizar dados desnecessários
    const dataToSend = {
      name: updatedContact.name,
      phone: updatedContact.phone,
      bot_status: updatedContact.bot_status,
      conversation_history: updatedContact.conversation_history,
      label: updatedContact.label,
    };

    // Atualizar apenas os campos necessários
    const response = await api.patch(
      `${CONTACTS_URL}${updatedContact.id}/`,
      dataToSend
    );
    const updatedContactFromAPI = response.data;

    // Atualizar estado local de forma mais eficiente
    const columnId = updatedContactFromAPI.column;
    setContactsByColumn((prev) => ({
      ...prev,
      [columnId]:
        prev[columnId]?.map((contact) =>
          contact.id === updatedContactFromAPI.id
            ? updatedContactFromAPI
            : contact
        ) || [],
    }));

    setContactModalOpen(false);
    setCurrentContact(null);

    Swal.fire({
      title: "Sucesso!",
      text: "Contato atualizado com sucesso!",
      icon: "success",
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 3000,
    });
  } catch (err) {
    console.error("Erro ao salvar contato:", err);
    Swal.fire({
      title: "Erro!",
      text: "Não foi possível salvar as alterações do contato.",
      icon: "error",
      confirmButtonText: "OK",
    });
  }
};
